'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import type { AgentsCardContent } from '@/lib/services'
import { AppTile } from './app-tile'

const TICK_MS = 3200

/** The rows on screen after `tick` new ones arrived: newest first, one per time. */
export function feedRows(card: AgentsCardContent, tick: number) {
  const count = card.items.length
  const shown = Math.min(card.times.length, count)
  return Array.from({ length: shown }, (_, position) => {
    const index = (((position - tick) % count) + count) % count
    return { index, item: card.items[index] }
  })
}

/** A row's text with its leading figure apart, so the figure can take the brand blue. */
export function splitFigure(text: string): [figure: string | null, rest: string] {
  const match = /^([\d.,]+)\s+(.+)$/.exec(text)
  return match ? [match[1], match[2]] : [null, text]
}

/**
 * Hero card of the Digital Enterprise page: agents at work on KAI Vault,
 * one row per piece of work (the app it ran in, what was done with its
 * figure in brand blue, how long ago, and its status pill; the newest still
 * running). Every few seconds a new row arrives at the top and the oldest
 * leaves, while the card is on screen. The server renders the first rows,
 * and under reduced motion they stay put. The maze logo marks the title and
 * sits faintly behind the card. Labelled "Example" until the rows are
 * approved data.
 */
export function AgentsCard({ card }: { card: AgentsCardContent }) {
  const [tick, setTick] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    let timer: ReturnType<typeof setInterval> | undefined
    const stop = () => {
      clearInterval(timer)
      timer = undefined
    }
    const observer = new IntersectionObserver(([entry]) => {
      stop()
      if (entry.isIntersecting) timer = setInterval(() => setTick((value) => value + 1), TICK_MS)
    })
    observer.observe(element)
    return () => {
      observer.disconnect()
      stop()
    }
  }, [])

  const rows = feedRows(card, tick)
  // The row that just left slides out under the others; it is only motion.
  const leaving = tick > 0 ? feedRows(card, tick - 1).at(-1) : undefined
  return (
    <div className="de-agents" ref={ref}>
      {/* eslint-disable-next-line @next/next/no-img-element -- decorative watermark, sized by CSS */}
      <img className="de-agents-watermark" src="/icon.svg" alt="" aria-hidden="true" />
      <div className="de-card de-agents-card">
        <div className="de-feed-head">
          <p className="de-feed-title">
            {/* eslint-disable-next-line @next/next/no-img-element -- the maze logo as a small mark */}
            <img className="de-feed-mark" src="/icon.svg" alt="" aria-hidden="true" />
            {card.title}
          </p>
          <span className="de-feed-when">{card.subtitle}</span>
        </div>
        <div className="de-feed-window">
          <ol key={tick} className={tick > 0 ? 'de-feed-rows de-feed-rows--arrive' : 'de-feed-rows'}>
            {rows.map(({ index, item }, position) => (
              <FeedRow
                key={index}
                text={item.text}
                icon={<AppTile icon={item.icon} />}
                time={card.times[position]}
                status={position === 0 ? 'running' : 'done'}
                label={position === 0 ? card.running : card.done}
                fresh={position === 1 && tick > 0}
              />
            ))}
            {leaving ? (
              <FeedRow
                key={`leaving-${leaving.index}`}
                text={leaving.item.text}
                icon={<AppTile icon={leaving.item.icon} />}
                time={card.times[rows.length - 1]}
                status="done"
                label={card.done}
                leaving
              />
            ) : null}
          </ol>
        </div>
        <p className="de-card-example">{card.example}</p>
      </div>
    </div>
  )
}

/** One piece of work: its app, the text with its figure, when, and its status pill. */
function FeedRow({
  text,
  icon,
  time,
  status,
  label,
  fresh = false,
  leaving = false,
}: {
  text: string
  icon: ReactNode
  time: string
  status: 'running' | 'done'
  label: string
  fresh?: boolean
  leaving?: boolean
}) {
  const [figure, rest] = splitFigure(text)
  return (
    <li className={leaving ? 'de-feed-row de-feed-row--leaving' : 'de-feed-row'} aria-hidden={leaving || undefined}>
      {icon}
      <span className="de-feed-body">
        <span className="de-feed-text">
          {figure ? <strong className="de-feed-figure">{figure}</strong> : null}
          {figure ? ' ' : null}
          {rest}
        </span>
        <span className="de-feed-time">{time}</span>
      </span>
      <span
        className={
          status === 'running'
            ? 'de-pill de-pill--live de-pill--running'
            : fresh
              ? 'de-pill de-pill--done de-pill--fresh'
              : 'de-pill de-pill--done'
        }
      >
        {status === 'running' ? <span className="de-running-dot" aria-hidden="true" /> : <Check aria-hidden="true" strokeWidth={3} />}
        {label}
      </span>
    </li>
  )
}
